import React, { useCallback, useEffect } from "react";
import moment from "moment";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid2,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
    Tooltip,
    IconButton,
    Button,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import DirectionsWalkRoundedIcon from "@mui/icons-material/DirectionsWalkRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import BedtimeRoundedIcon from "@mui/icons-material/BedtimeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import { hasAccess, httpRequest, onRequestError } from "../../helper/global";
import { PERMISSIONS } from "../../helper/constant";
import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import { set } from "date-fns";
import { ca } from "date-fns/locale";

/**
 * Main UI
 */
export function AIHealthInsightCard({
    dispatch,
    navigate,
    theme,
    user,
    permissionConfig,
    patient,
}) {
    const [aiData, setAiData] = React.useState(null);
    const [daysAgo, setDaysAgo] = React.useState(30);
    const [windowLabel, setWindowLabel] = React.useState("");

    const onRefresh = useCallback(
        async (cache = false) => {
            if (!user || !permissionConfig) {
                return;
            }

            if (
                !hasAccess(
                    user,
                    permissionConfig,
                    "healthData",
                    PERMISSIONS.read,
                )
            ) {
                return;
            }

            try {
                dispatch(showLoader({ message: "Analyzing health data..." }));
                const result = await httpRequest(
                    `ai/request/analyze-data`,
                    {
                        patientId: patient.id,
                        daysAgo: daysAgo,
                        cache: cache,
                    },
                    "POST",
                    (error) => {
                        dispatch(hideLoader());
                        onRequestError(dispatch, navigate, {
                            title: error.title,
                            message: error.message,
                        });
                    },
                );

                dispatch(hideLoader());

                if (result === false) {
                    return;
                }

                setAiData(result.payload);
                setWindowLabel(`Last ${daysAgo} days`);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        [daysAgo],
    );

    useEffect(() => {
        onRefresh(true);
    }, []);

    // If no AI data, show empty state with analyze button
    if (!aiData) {
        return (
            <Card
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                <CardHeader
                    title={
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            <Chip
                                label="AI Coach"
                                size="small"
                                sx={{ fontWeight: 600 }}
                                variant="outlined"
                            />
                        </Stack>
                    }
                    sx={{
                        pb: 1,
                        "& .MuiCardHeader-title": { lineHeight: 1.2 },
                    }}
                />
                <Divider />
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 4,
                        }}
                    >
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            No AI Coach insights available yet.
                        </Typography>

                        {hasAccess(
                            user,
                            permissionConfig,
                            "ai",
                            PERMISSIONS.write,
                        ) && (
                            <Button
                                variant="contained"
                                onClick={() => onRefresh()}
                                startIcon={<RefreshIcon />}
                            >
                                Analyze Data
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    }

    const prettyDate = moment(aiData.date).format("MMM DD, YYYY, hh:mm A");

    return (
        <Card
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            <CardHeader
                title={
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <Chip
                            label="AI Coach"
                            size="small"
                            sx={{ fontWeight: 600 }}
                            variant="outlined"
                        />
                        <Chip
                            label={windowLabel}
                            size="small"
                            sx={{ fontWeight: 600 }}
                            variant="filled"
                        />

                        {hasAccess(
                            user,
                            permissionConfig,
                            "ai",
                            PERMISSIONS.write,
                        ) && (
                            <Tooltip title="Refresh insights">
                                <IconButton
                                    size="small"
                                    onClick={() => onRefresh()}
                                    aria-label="Refresh insights"
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                }
                subheader={
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        {prettyDate && (
                            <Typography variant="body2" color="text.secondary">
                                Generated {prettyDate}
                            </Typography>
                        )}
                    </Stack>
                }
                sx={{
                    pb: 1,
                    "& .MuiCardHeader-title": { lineHeight: 1.2 },
                }}
            />

            <Divider />

            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Top summary */}
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                        Summary
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.primary"
                        sx={{ lineHeight: 1.6 }}
                    >
                        {aiData.window_summary}
                    </Typography>
                </Box>

                <Grid2 spacing={2.25} container>
                    {/* Body composition */}
                    <Grid2 size={{ xs: 12 }}>
                        <Card
                            elevation={0}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                                height: "100%",
                            }}
                        >
                            <CardContent sx={{ p: 2.25 }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <InsightsRoundedIcon fontSize="small" />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Body composition insight
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.65 }}
                                >
                                    {aiData.body_composition_insight}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>

                    {/* Activity */}
                    <Grid2 size={{ xs: 12 }}>
                        <Card
                            elevation={0}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                                height: "100%",
                                width: "100%",
                            }}
                        >
                            <CardContent sx={{ p: 2.25 }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <DirectionsWalkRoundedIcon fontSize="small" />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Activity insight
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.65 }}
                                >
                                    {aiData.activity_insight}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>

                    {/* Positive highlight */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                                p: 2.25,
                                width: "100%",
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 0.75 }}
                            >
                                <EmojiEventsRoundedIcon fontSize="small" />
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Positive highlight
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ lineHeight: 1.65 }}
                            >
                                {aiData.positive_highlight}
                            </Typography>
                        </Box>
                    </Grid2>

                    {/* Sleep highlight */}
                    <Grid2 size={{ xs: 12 }}>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                                p: 2.25,
                                width: "100%",
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 0.75 }}
                            >
                                <BedtimeRoundedIcon fontSize="small" />
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Sleep highlight
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ lineHeight: 1.65 }}
                            >
                                {aiData.sleep_highlight ??
                                    "Not enough sleep data to generate insight."}
                            </Typography>
                        </Box>
                    </Grid2>

                    {/* Tips */}
                    <Grid2 size={{ xs: 12 }}>
                        <Card
                            elevation={0}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 3,
                                width: "100%",
                            }}
                        >
                            <CardContent sx={{ p: 2.25 }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <TipsAndUpdatesRoundedIcon fontSize="small" />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Recommended tips
                                    </Typography>
                                </Stack>

                                <List disablePadding sx={{ mt: 0.5 }}>
                                    {(aiData.recommended_tips ?? []).map(
                                        (tip, idx) => (
                                            <ListItem
                                                key={`${idx}-${tip.slice(
                                                    0,
                                                    16,
                                                )}`}
                                                disableGutters
                                                sx={{
                                                    alignItems: "flex-start",
                                                    py: 0.75,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 34,
                                                        mt: "3px",
                                                    }}
                                                >
                                                    <CheckCircleRoundedIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                lineHeight: 1.65,
                                                            }}
                                                        >
                                                            {tip}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ),
                                    )}
                                </List>

                                <Divider sx={{ my: 1.75 }} />

                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 800, mb: 0.5 }}
                                >
                                    Encouragement
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.65 }}
                                >
                                    {aiData.encouragement}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
}
